import {
    NewsletterManageResponse,
    NewsletterManageConfirmationRow,
    NewsletterManageUpdateMessage,
    NewsletterSummary
} from '../common/models/newslettermanage/newsletter-subscription-response.model';
import { formatNewsletterKeyForDisplay } from '../common/utils/newsletter-display.util';
import { VueLogger } from 'vue-logger-plugin/dist/types/logger';
import { FetchService } from './fetch.service';
import {
    BELIEFNET_ASSETS_BASE,
    BELIEFNET_NEWSLETTERS_API_BASE,
    beliefnetNewsletterSubscriptionsUrl,
    beliefnetNewsletterSubscriptionItemUrl
} from './global-api.constants';
import { buildSubscribeRequestBody } from './newsletter-api.util';

function confirmationRow(key: string): NewsletterManageConfirmationRow {
    return { DisplayName: formatNewsletterKeyForDisplay(key), ListId: key };
}

export class NewsletterManageService {
    private logger: VueLogger;
    private fetchService: FetchService;

    constructor(logger: VueLogger, fetchService: FetchService) {
        this.logger = logger;
        this.fetchService = fetchService;
    }

    /**
     * Subscribe user to newsletters (Umbraco Global API v2).
     */
    async subscribe(
        emailAddress: string,
        firstName: string,
        sourceDesc: string,
        rsid: string,
        newsletters: string[],
        partners = false
    ): Promise<NewsletterManageResponse | null> {
        this.logger.debug('Posting subscription request to Umbraco newsletters API');

        try {
            const body = buildSubscribeRequestBody(newsletters, {
                partners,
                rsid: rsid?.trim() || undefined,
                source: sourceDesc || undefined,
                firstName: firstName?.trim() || undefined
            });
            if (!body) {
                return { processed: false };
            }

            const url = beliefnetNewsletterSubscriptionsUrl(emailAddress);
            const result = await this.fetchService.post<unknown>(url, body, 'application/json');
            return { processed: result.ok };
        } catch (error) {
            this.logger.error('subscribe failed: ', error);
        }

        return null;
    }

    /**
     * Fetch all newsletters available for this site from the Umbraco Global API.
     * Normalizes relative thumbnailUrl values to absolute URLs.
     */
    async getAvailableNewsletters(): Promise<NewsletterSummary[]> {
        try {
            const result = await this.fetchService.get<NewsletterSummary[]>(BELIEFNET_NEWSLETTERS_API_BASE);
            if (result.ok && result.data) {
                return result.data.map((n) => ({
                    ...n,
                    thumbnailUrl:
                        n.thumbnailUrl && !n.thumbnailUrl.startsWith('http')
                            ? `${BELIEFNET_ASSETS_BASE}${n.thumbnailUrl}`
                            : n.thumbnailUrl
                }));
            }
        } catch (error) {
            this.logger.error('getAvailableNewsletters failed: ', error);
        }
        return [];
    }

    /**
     * Client-side confirmation labels for subscribed keys (replaces legacy newsletter confirmation API).
     */
    confirmationDisplayRows(newsletterKeys: string[]): NewsletterManageConfirmationRow[] {
        return newsletterKeys.map((k) => confirmationRow(String(k)));
    }

    /**
     * Returns whether the email is a known subscriber, plus the keys they are subscribed to.
     * A 404 (email not found) returns processed: false with an empty subscribedKeys array — not an error.
     */
    async checkEmailSubscriptions(emailAddress: string): Promise<NewsletterManageResponse | null> {
        this.logger.debug('GET newsletter subscriptions for email');

        try {
            const url = beliefnetNewsletterSubscriptionsUrl(emailAddress);
            const result = await this.fetchService.get<string[]>(url);

            if (result.status === 404) {
                return { processed: false, subscribedKeys: [] };
            }

            if (result.ok && result.data !== null) {
                return { processed: true, subscribedKeys: result.data };
            }
        } catch (error) {
            this.logger.error('check email subscription failed: ', error);
        }

        return { processed: false, subscribedKeys: [] };
    }

    /**
     * Preference center: sync selections via GET + POST (adds) + DELETE (removes).
     * Two-argument overload (retention flows): POST subscribe only for the given keys.
     *
     * GET returns Umbraco slugs; add/remove diff compares checkbox values to that list. For correct
     * preference sync, checkbox values should be slugs. Sitecore manage markup uses GetResponse campaign
     * IDs — POST adds route via campaignIds, but diff may be wrong until checkboxes use slugs or a map exists.
     */
    async updateEmailSubscription(
        emailAddress: string,
        newsletters: string[],
        allNewsletters?: string[]
    ): Promise<NewsletterManageResponse | null> {
        this.logger.debug('Updating email subscriptions via Umbraco newsletters API');

        try {
            const selected = newsletters.map((k) => String(k).trim()).filter(Boolean);

            if (!allNewsletters || allNewsletters.length === 0) {
                if (selected.length === 0) {
                    return { processed: false };
                }
                const body = buildSubscribeRequestBody(selected, {
                    partners: true,
                    source: 'retention'
                });
                if (!body) {
                    return { processed: false };
                }
                const url = beliefnetNewsletterSubscriptionsUrl(emailAddress);
                const postRes = await this.fetchService.post<unknown>(url, body, 'application/json');
                const message: NewsletterManageUpdateMessage = {
                    new: postRes.ok ? selected.map(confirmationRow) : [],
                    removed: [],
                    retention: []
                };
                return { processed: postRes.ok, message };
            }

            const priorRes = await this.fetchService.get<string[]>(beliefnetNewsletterSubscriptionsUrl(emailAddress));
            if (!priorRes.ok || priorRes.data === null) {
                return { processed: false };
            }

            const prior = priorRes.data.map((k) => String(k).trim());
            const universe = allNewsletters.map((k) => String(k).trim()).filter(Boolean);

            const toAdd = selected.filter((k) => !prior.includes(k));
            const toRemove = prior.filter((k) => universe.includes(k) && !selected.includes(k));

            if (toAdd.length > 0) {
                const body = buildSubscribeRequestBody(toAdd, {
                    partners: true,
                    source: 'manage-preferences'
                });
                if (!body) {
                    return { processed: false };
                }
                const postRes = await this.fetchService.post<unknown>(
                    beliefnetNewsletterSubscriptionsUrl(emailAddress),
                    body,
                    'application/json'
                );
                if (!postRes.ok) {
                    return { processed: false };
                }
            }

            for (const key of toRemove) {
                const delUrl = beliefnetNewsletterSubscriptionItemUrl(emailAddress, key);
                const delRes = await this.fetchService.delete<unknown>(delUrl);
                if (!delRes.ok) {
                    return { processed: false };
                }
            }

            const message: NewsletterManageUpdateMessage = {
                new: toAdd.map(confirmationRow),
                removed: toRemove.map(confirmationRow),
                retention: []
            };

            return { processed: true, message };
        } catch (error) {
            this.logger.error('update email subscription failed: ', error);
        }

        return null;
    }
}
